import React, { useState, useRef, Fragment } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Lock, HelpCircle, Settings, Info, ChevronRight, ChevronLeft, ShoppingCart, ArrowRight } from 'lucide-react';
import { InventoryLayout } from '../../components/inventory/InventoryLayout';
import { OrderRequestForm } from '../../components/inventory/OrderRequestForm';
import { ProductPlaceholder } from '../../components/ProductPlaceholder';
import { ProductCard } from '../../components/inventory/ProductCard';
import { products } from '../../data/products';
import { isRestrictedProduct } from '../../data/inventory';
export function InventoryProductPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const product = products.find(p => p.slug === slug);
  const [activeImage, setActiveImage] = useState(0);
  const orderFormRef = useRef<HTMLDivElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  if (!product) {
    return <Navigate to="/" replace data-id="element-530" />;
  }
  const restricted = isRestrictedProduct(product);
  // Similar products: same category, exclude current, up to 8
  const similarProducts = products.filter(p => p.category === product.category && p.slug !== product.slug).slice(0, 8);
  const scrollToOrderForm = () => {
    orderFormRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const scrollCarousel = (direction: 'left' | 'right') => {
    if (!carouselRef.current) return;
    const scrollAmount = 320;
    carouselRef.current.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth'
    });
  };
  return <InventoryLayout data-id="element-531">
      {/* 1. Breadcrumb */}
      <div className="bg-white border-b border-steel-100 py-3 sm:py-4" data-id="element-532">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-533">
          <div className="flex items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs font-bold uppercase tracking-wider text-steel-500 overflow-x-auto scrollbar-hide" data-id="element-534">
            <Link to="/" className="hover:text-cg-orange-600 whitespace-nowrap flex-shrink-0" data-id="element-535">
              Inventory
            </Link>
            <ChevronRight className="w-3 h-3 flex-shrink-0" data-id="element-536" />
            <Link to={`/inventory/${product.category}`} className="hover:text-cg-orange-600 whitespace-nowrap flex-shrink-0" data-id="element-537">
              {product.categoryName}
            </Link>
            <ChevronRight className="w-3 h-3 flex-shrink-0" data-id="element-538" />
            <span className="text-navy-900 truncate" data-id="element-539">{product.name}</span>
          </div>
        </div>
      </div>

      <div className="bg-white py-8 sm:py-12" data-id="element-540">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-541">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12" data-id="element-542">
            {/* LEFT COLUMN: Gallery */}
            <div className="lg:col-span-5" data-id="element-543">
              <div className="lg:sticky lg:top-[140px] lg:self-start" data-id="element-544">
                <div className="aspect-[4/3] bg-slate-100 rounded-sm overflow-hidden border border-steel-200 mb-3 sm:mb-4 relative" data-id="element-545">
                  <ProductPlaceholder aspectRatio="4/3" className="w-full h-full" data-id="element-546" />
                  <div className="absolute top-3 left-3 sm:top-4 sm:left-4 flex flex-col gap-2" data-id="element-547">
                    {restricted && <span className="bg-red-600 text-white text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 sm:px-3 py-1 sm:py-1.5 rounded-sm shadow-sm flex items-center gap-1.5" data-id="element-548">
                        <Lock className="w-3 h-3" data-id="element-549" /> Restricted Item
                      </span>}
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2 sm:gap-4" data-id="element-550">
                  {[0, 1, 2, 3].map(i => <button key={i} onClick={() => setActiveImage(i)} className={`aspect-square bg-slate-50 border rounded-sm overflow-hidden transition-all ${activeImage === i ? 'border-cg-orange-600 ring-1 ring-cg-orange-600' : 'border-steel-200 hover:border-steel-400'}`} data-id="element-551">
                      <ProductPlaceholder aspectRatio="1/1" className="w-full h-full" data-id="element-552" />
                    </button>)}
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN: Product Info */}
            <div className="lg:col-span-7" data-id="element-553">
              <div className="mb-6" data-id="element-554">
                <h1 className="text-2xl sm:text-3xl font-bold text-navy-900 mb-2 leading-tight" data-id="element-555">
                  {product.name}
                </h1>
                <p className="text-base text-steel-600 font-medium" data-id="element-556">
                  {product.shortDescription}
                </p>
                {restricted && <p className="text-xs text-red-700 font-bold mt-2" data-id="element-557">
                    Restricted item. Eligibility may be required.
                  </p>}
              </div>

              {/* Mobile CTA (Hidden on Desktop) */}
              <div className="lg:hidden mb-8" data-id="element-558">
                <button onClick={scrollToOrderForm} className="w-full bg-cg-orange-600 hover:bg-cg-orange-500 text-white font-bold uppercase tracking-wider py-3.5 rounded-sm text-sm flex items-center justify-center gap-2 transition-colors shadow-sm" data-id="element-559">
                  <ShoppingCart className="w-4 h-4" data-id="element-560" />
                  Request an Order
                </button>
                <p className="text-xs text-steel-500 text-center mt-2" data-id="element-561">
                  Availability and fulfilment are confirmed during review.
                </p>
              </div>

              {/* Desktop CTA (Hidden on Mobile) */}
              <div className="hidden lg:block mb-8 bg-slate-50 border border-steel-200 rounded-sm p-5" data-id="element-562">
                <div className="flex items-center justify-between gap-4" data-id="element-563">
                  <div data-id="element-564">
                    <h3 className="text-sm font-bold text-navy-900 mb-1" data-id="element-565">
                      Ready to order?
                    </h3>
                    <p className="text-xs text-steel-500" data-id="element-566">
                      No payment taken. Availability confirmed during review.
                    </p>
                  </div>
                  <button onClick={scrollToOrderForm} className="flex-shrink-0 bg-cg-orange-600 hover:bg-cg-orange-500 text-white font-bold uppercase tracking-wider py-3 px-6 rounded-sm text-sm flex items-center justify-center gap-2 transition-colors shadow-sm" data-id="element-567">
                    <ShoppingCart className="w-4 h-4" data-id="element-568" />
                    Request an Order
                  </button>
                </div>
              </div>

              <div className="mb-6 border-b border-steel-100 pb-6" data-id="element-569">
                <h2 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-570">
                  <Info className="w-4 h-4 text-cg-orange-600" data-id="element-571" />
                  Product Overview
                </h2>
                <div className="text-sm text-steel-600 leading-relaxed space-y-4" data-id="element-572">
                  <p data-id="element-573">{product.technicalDescription}</p>
                  <p data-id="element-574">
                    Configuration and supply options are confirmed against
                    operational requirements and ordering volumes. This item is
                    designed for professional use in{' '}
                    {product.sectors.join(', ')} environments, ensuring
                    durability and compliance with relevant standards.
                  </p>
                </div>
              </div>

              <div data-id="element-575">
                <h2 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-576">
                  <Settings className="w-4 h-4 text-cg-orange-600" data-id="element-577" />
                  Specifications
                </h2>
                <div className="bg-slate-50 border border-steel-200 rounded-sm p-4" data-id="element-578">
                  <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2" data-id="element-579">
                    {product.specifications.map((spec, i) => <Fragment key={i} data-id="element-580">
                        <div className="text-xs font-bold text-steel-500 uppercase tracking-wider text-right" data-id="element-581">
                          {spec.field}:
                        </div>
                        <div className="text-sm text-navy-900 font-medium" data-id="element-582">
                          {spec.value}
                        </div>
                      </Fragment>)}
                  </div>
                </div>
                <p className="text-xs text-steel-500 mt-2 italic" data-id="element-583">
                  * Full specification and documentation can be provided on
                  request.
                </p>
              </div>
            </div>
          </div>

          {/* Full Width Order Form Section */}
          <div ref={orderFormRef} className="mt-8 lg:mt-12" data-id="element-584">
            <OrderRequestForm productName={product.name} productSlug={product.slug} restricted={restricted} data-id="element-585" />
            <div className="mt-6 flex items-center justify-center gap-2 text-sm text-steel-500" data-id="element-586">
              <HelpCircle className="w-4 h-4 flex-shrink-0" data-id="element-587" />
              <span data-id="element-588">Need help selecting the right option?</span>
              <Link to="/contact" className="font-bold text-cg-orange-600 hover:underline" data-id="element-589">
                Contact Support
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Similar Products — Premium Carousel */}
      {similarProducts.length > 0 && <div className="bg-white py-12 sm:py-16 border-t border-steel-100" data-id="element-590">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-591">
            <div className="flex items-end justify-between mb-8" data-id="element-592">
              <div className="border-l-2 border-cg-orange-600 pl-4" data-id="element-593">
                <h2 className="text-lg sm:text-xl font-bold text-navy-900" data-id="element-594">
                  Similar Products
                </h2>
                <p className="text-sm text-steel-500 mt-0.5" data-id="element-595">
                  More items from this category.
                </p>
              </div>
              <div className="flex items-center gap-3" data-id="element-596">
                <Link to={`/inventory/${product.category}`} className="hidden sm:flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-navy-900 hover:text-cg-orange-600 transition-colors group" data-id="element-597">
                  View All in {product.categoryName}
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" data-id="element-598" />
                </Link>
                <div className="hidden lg:flex items-center gap-1.5" data-id="element-599">
                  <button onClick={() => scrollCarousel('left')} className="w-9 h-9 flex items-center justify-center border border-steel-200 rounded-sm text-steel-500 hover:border-cg-orange-600 hover:text-cg-orange-600 transition-colors" aria-label="Scroll left" data-id="element-600">
                    <ChevronLeft className="w-4 h-4" data-id="element-601" />
                  </button>
                  <button onClick={() => scrollCarousel('right')} className="w-9 h-9 flex items-center justify-center border border-steel-200 rounded-sm text-steel-500 hover:border-cg-orange-600 hover:text-cg-orange-600 transition-colors" aria-label="Scroll right" data-id="element-602">
                    <ChevronRight className="w-4 h-4" data-id="element-603" />
                  </button>
                </div>
              </div>
            </div>

            <div className="-mx-4 px-4 sm:mx-0 sm:px-0" data-id="element-604">
              <div ref={carouselRef} className="flex gap-5 overflow-x-auto scrollbar-hide snap-x snap-mandatory pb-4 sm:pb-0" data-id="element-605">
                {similarProducts.map(p => <div key={p.slug} className="flex-shrink-0 w-[72%] sm:w-[280px] lg:w-[calc(25%-15px)] snap-start" data-id="element-606">
                    <ProductCard product={p} data-id="element-607" />
                  </div>)}
              </div>
            </div>

            <div className="mt-6 sm:hidden text-center" data-id="element-608">
              <Link to={`/inventory/${product.category}`} className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-cg-orange-600 hover:text-cg-orange-700 transition-colors" data-id="element-609">
                View All in {product.categoryName}
                <ArrowRight className="w-3.5 h-3.5" data-id="element-610" />
              </Link>
            </div>
          </div>
        </div>}
    </InventoryLayout>;
}